import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { ColorPreference, useColorMode } from "src/design/ColorModeProvider";
import { motion, radius } from "src/design/tokens";
import { FilterPills } from "src/grading/components/FilterPills/FilterPills";

export interface ColorModeToggleProps {
  variant: "switch" | "segmented";
}

const PREFERENCES: ColorPreference[] = ["light", "dark", "system"];

/**
 * Gym-mode (dark theme) control. `switch` is the header pill from the design: a surfaceAlt
 * pill holding the 40×22 track and the label "Gym mode" / "Gym mode on".
 */
export const ColorModeToggle = ({ variant }: ColorModeToggleProps) => {
  const { t } = useTranslation();
  const { mode, preference, setPreference, reducedMotion } = useColorMode();

  if (variant === "segmented") {
    return (
      <FilterPills
        aria-label={t("theme.appearance")}
        value={preference}
        options={PREFERENCES.map((value) => ({
          value,
          label: t(`theme.${value}`),
        }))}
        onChange={(next) => setPreference(next as ColorPreference)}
      />
    );
  }

  const isDark = mode === "dark";
  const modeLabel = isDark
    ? t("theme.gymModeOn", { defaultValue: "Gym mode on" })
    : t("theme.gymMode");
  const stateLabel = isDark ? t("theme.dark") : t("theme.light");
  const transition = reducedMotion ? "none" : `all ${motion.fast}ms ease`;

  return (
    <Box
      component="button"
      type="button"
      role="switch"
      aria-checked={isDark}
      // The visible word is part of the name (WCAG 2.5.3 Label in Name).
      aria-label={`${modeLabel}: ${stateLabel}`}
      onClick={() => setPreference(isDark ? "light" : "dark")}
      sx={(theme) => ({
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        minHeight: 36,
        p: "6px 10px 6px 6px",
        border: 0,
        borderRadius: `${radius.pill}px`,
        bgcolor: theme.palette.surfaceAlt,
        font: "inherit",
        cursor: "pointer",
        color: theme.palette.text.primary,
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.focusRing}`,
          outlineOffset: 2,
        },
      })}
    >
      <Box
        aria-hidden
        sx={(theme) => ({
          position: "relative",
          flex: "none",
          width: 40,
          height: 22,
          borderRadius: `${radius.pill}px`,
          backgroundColor: isDark
            ? theme.palette.primary.main
            : theme.palette.track,
          transition,
        })}
      >
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: 2,
            left: 2,
            width: 18,
            height: 18,
            borderRadius: "50%",
            backgroundColor: theme.palette.onTier,
            transform: isDark ? "translateX(18px)" : "translateX(0)",
            transition,
          })}
        />
      </Box>
      <Box
        component="span"
        sx={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
      >
        {modeLabel}
      </Box>
    </Box>
  );
};

export default ColorModeToggle;
